import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Alert, Button, Modal, Progress } from "antd";
import { toast } from "react-toastify";

import CmsThemeProvider from "../../Components/cms/CmsThemeProvider";
import Head from "../../Components/Head";
import { paths } from "../../routes/paths";
import {
  uploadAndRegisterVideoScene,
  useGetTourDetail,
  useSaveTour,
  useUploadTourScene,
} from "../../api/tours";
import PanoramaEditorViewer from "./components/PanoramaEditorViewer";
import ScenesSidebarSection from "./components/ScenesSidebarSection";
import HotspotsSidebarSection from "./components/HotspotsSidebarSection";
import HotspotEditorPanel from "./components/HotspotEditorPanel";
import {
  ACCEPTED_IMAGE_TYPES,
  createEmptyTour,
  createHotspotDraft,
  createSceneFromUpload,
  deleteHotspot,
  deleteScene,
  getApiErrorMessage,
  getSceneById,
  getTargetSceneName,
  hydrateTourFromApi,
  isFullUrl,
  isPersistedId,
  isTextHotspot,
  mapSceneFromApi,
  normalizeHotspotForSave,
  openHotspotUrl,
  renameScene,
  reorderScenes,
  revokeHotspotDraftUrls,
  revokeTourUrls,
  SCENE_PROJECTIONS,
  serializeTourForSave,
  stripExtension,
  upsertHotspot,
  updateSceneDefaultView,
} from "./utils/tourEditorUtils";
import {
  formatBytes,
  probeAndPrepareVideo,
  revokeVideoProbeUrls,
  validateVideoFile,
} from "./utils/videoProbe";
import styles from "./TourEditorPage.module.css";

const TourEditorPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const titleParam = searchParams.get("title") || "";
  const tourIdParam = Number(searchParams.get("Id"));
  const tourId = isPersistedId(tourIdParam) ? tourIdParam : null;

  const [tour, setTour] = useState(() => {
    const emptyTour = createEmptyTour();
    emptyTour.id = tourId;
    if (titleParam) emptyTour.title = titleParam;
    return emptyTour;
  });
  const tourRef = useRef(tour);
  const viewerRef = useRef(null);
  const hydratedIdRef = useRef(null);
  const [activeSceneId, setActiveSceneId] = useState(null);
  const [transitionKey, setTransitionKey] = useState(0);
  const [addHotspotMode, setAddHotspotMode] = useState(false);
  const [editingHotspot, setEditingHotspot] = useState(null);
  const [editingHotspotIsNew, setEditingHotspotIsNew] = useState(false);
  const [hotspotErrors, setHotspotErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  const [isDirty, setIsDirty] = useState(false);
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  const saveStatusTimerRef = useRef(null);
  const isDirtyRef = useRef(false);
  const editingHotspotRef = useRef(null);
  const editingHotspotOriginalRef = useRef(null);
  const leavingRef = useRef(false);
  const videoAbortControllerRef = useRef(null);
  const [videoUploadState, setVideoUploadState] = useState({
    active: false,
    fileName: "",
    fileSize: 0,
    stage: "",
    progress: 0,
    warning: null,
  });

  const {
    data: tourData,
    isLoading: isTourLoading,
    isFetching: isTourFetching,
    isError: isTourError,
    error: tourError,
  } = useGetTourDetail(tourId);
  const { mutateAsync: uploadScene } = useUploadTourScene(tourId);
  const { mutateAsync: saveTour, isLoading: isSaving } = useSaveTour(tourId);

  tourRef.current = tour;
  isDirtyRef.current = isDirty;
  editingHotspotRef.current = editingHotspot;

  const activeScene = useMemo(
    () => getSceneById(tour, activeSceneId),
    [tour, activeSceneId]
  );

  const applyTourFromApi = useCallback((data, { keepActive = true } = {}) => {
    setTour(prev => {
      revokeTourUrls(prev);
      return hydrateTourFromApi(data);
    });
    const scenes = data?.scenes ?? [];
    setActiveSceneId(current => {
      if (keepActive && current && scenes.some(scene => scene.id === current)) {
        return current;
      }
      return data?.startSceneId ?? scenes[0]?.id ?? null;
    });
    setIsDirty(false);
  }, []);

  useEffect(() => {
    if (!tourData?.id || hydratedIdRef.current === tourData.id) return;
    hydratedIdRef.current = tourData.id;
    applyTourFromApi(tourData, { keepActive: false });
    setTransitionKey(key => key + 1);
  }, [tourData, applyTourFromApi]);

  useEffect(() => {
    return () => {
      revokeTourUrls(tourRef.current);
      if (saveStatusTimerRef.current) clearTimeout(saveStatusTimerRef.current);
    };
  }, []);

  const updateTour = useCallback(updater => {
    setTour(prev => (typeof updater === "function" ? updater(prev) : updater));
    setIsDirty(true);
  }, []);

  const hasUnsavedChanges = () =>
    isDirtyRef.current || Boolean(editingHotspotRef.current);

  const leaveEditor = () => {
    leavingRef.current = true;
    isDirtyRef.current = false;
    editingHotspotRef.current = null;
    setIsDirty(false);
    setLeaveModalOpen(false);
    if (window.opener) {
      window.close();
      return;
    }
    navigate(paths.admin.tours.root);
  };

  const handleBack = () => {
    if (hasUnsavedChanges()) {
      setLeaveModalOpen(true);
      return;
    }
    leaveEditor();
  };

  const handleDiscardAndLeave = () => {
    leaveEditor();
  };

  const validateHotspot = hotspot => {
    const errors = {};
    if (isTextHotspot(hotspot)) {
      if (hotspot.webUrl?.trim() && !isFullUrl(hotspot.webUrl)) {
        errors.webUrl = "Enter a full URL starting with https://";
      }
    } else if (!isPersistedId(hotspot.targetSceneId)) {
      errors.targetSceneId = "Target scene is required";
    }
    return errors;
  };

  const isHotspotIncomplete = hotspot =>
    !isTextHotspot(hotspot) && !isPersistedId(hotspot.targetSceneId);

  const buildTourToSave = currentTour => {
    if (!editingHotspot || !activeSceneId) {
      return { nextTour: currentTour, hotspotErrors: null };
    }
    const errors = validateHotspot(editingHotspot);
    if (Object.keys(errors).length) {
      return { nextTour: currentTour, hotspotErrors: errors };
    }
    return {
      nextTour: upsertHotspot(
        currentTour,
        activeSceneId,
        normalizeHotspotForSave(editingHotspot, activeSceneId)
      ),
      hotspotErrors: null,
    };
  };

  const handleSaveTour = async () => {
    if (!tourId) {
      toast.error("Missing tour id. Create the tour from the 360 Tours list.");
      return false;
    }

    viewerRef.current?.cancelReposition?.();

    const { nextTour, hotspotErrors: openHotspotErrors } = buildTourToSave(tour);
    if (openHotspotErrors) {
      setHotspotErrors(openHotspotErrors);
      setLeaveModalOpen(false);
      toast.error("Finish the hotspot before saving.");
      return false;
    }
    if (!nextTour.title?.trim()) {
      toast.error("Title is required.");
      return false;
    }
    if (nextTour.scenes.some(scene => !isPersistedId(scene.id))) {
      toast.error("Wait for panorama uploads to finish before saving.");
      return false;
    }
    const incompleteHotspot = nextTour.scenes.some(scene =>
      (scene.hotspots ?? []).some(isHotspotIncomplete)
    );
    if (incompleteHotspot) {
      toast.error(
        "Navigation hotspots need a destination room."
      );
      return false;
    }

    setSaveStatus("Saving...");
    try {
      const res = await saveTour(serializeTourForSave(nextTour));
      const saved = res?.data?.data;
      if (saved) {
        hydratedIdRef.current = saved.id;
        applyTourFromApi(saved);
        setEditingHotspot(null);
        setEditingHotspotIsNew(false);
        setAddHotspotMode(false);
      }
      setSaveStatus(res?.data?.message || "Saved");
      toast.success(res?.data?.message || "360 Tour Saved");
      if (saveStatusTimerRef.current) clearTimeout(saveStatusTimerRef.current);
      saveStatusTimerRef.current = setTimeout(() => setSaveStatus(""), 2000);
      return true;
    } catch (err) {
      setSaveStatus("");
      toast.error(getApiErrorMessage(err, "Failed to save tour"));
      return false;
    }
  };

  const handleSaveAndLeave = async () => {
    const saved = await handleSaveTour();
    if (saved) leaveEditor();
  };

  useEffect(() => {
    const onBeforeUnload = event => {
      if (leavingRef.current || !hasUnsavedChanges()) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  const handleAddScenes = async (files, options = {}) => {
    if (!tourId) {
      toast.error("Missing tour id. Create the tour from the 360 Tours list.");
      return;
    }

    const projection =
      options?.projection || SCENE_PROJECTIONS.EQUIRECTANGULAR_360;

    const validFiles = Array.from(files).filter(file =>
      ACCEPTED_IMAGE_TYPES.includes(file.type)
    );
    if (!validFiles.length) {
      toast.error("Panorama must be a JPEG, PNG, or WebP image.");
      return;
    }

    setUploading(true);
    let lastAddedId = null;
    try {
      for (const file of validFiles) {
        const formData = new FormData();
        formData.append("image", file);
        const name = stripExtension(file.name);
        if (name) formData.append("name", name);
        formData.append("projection", projection);

        const res = await uploadScene(formData);
        const data = res?.data?.data;
        if (!isPersistedId(data?.id)) {
          toast.error("Scene upload did not return a scene id.");
          continue;
        }

        updateTour(prev => {
          const nextOrder = (prev.scenes?.length || 0) + 1;
          const newScene = createSceneFromUpload(file, data, nextOrder, { projection });
          lastAddedId = newScene.id;
          const scenes = [...prev.scenes, newScene];
          return {
            ...prev,
            scenes,
            startSceneId: scenes[0]?.id ?? null,
          };
        });
      }

      if (lastAddedId != null) {
        viewerRef.current?.cancelReposition?.();
        viewerRef.current?.requestDefaultLook?.();
        setActiveSceneId(lastAddedId);
        setTransitionKey(key => key + 1);
        setAddHotspotMode(false);
        setEditingHotspot(null);
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to upload panorama"));
    } finally {
      setUploading(false);
    }
  };

  const handleCancelVideoUpload = () => {
    if (videoAbortControllerRef.current) {
      videoAbortControllerRef.current.abort();
      videoAbortControllerRef.current = null;
    }
    setVideoUploadState({
      active: false,
      fileName: "",
      fileSize: 0,
      stage: "",
      progress: 0,
      warning: null,
    });
    setUploading(false);
    toast.info("360° video upload cancelled.");
  };

  const handleAddVideoScene = async file => {
    if (!tourId) {
      toast.error("Missing tour id. Create the tour from the 360 Tours list.");
      return;
    }

    if (uploading || videoUploadState.active) {
      toast.warn("An upload is already in progress. Please wait.");
      return;
    }

    const validation = validateVideoFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    const controller = new AbortController();
    videoAbortControllerRef.current = controller;

    setUploading(true);
    setVideoUploadState({
      active: true,
      fileName: file.name,
      fileSize: file.size,
      stage: "probing",
      progress: 0,
      warning: null,
    });

    let probeResult = null;
    try {
      probeResult = await probeAndPrepareVideo(file, {
        signal: controller.signal,
      });
    } catch (err) {
      if (
        err?.code === "ABORTED" ||
        err?.name === "AbortError" ||
        controller.signal.aborted
      ) {
        toast.info("360° video upload cancelled.");
      } else {
        toast.error(err?.message || "Failed to analyze 360° video.");
      }
      setUploading(false);
      setVideoUploadState({
        active: false,
        fileName: "",
        fileSize: 0,
        stage: "",
        progress: 0,
        warning: null,
      });
      videoAbortControllerRef.current = null;
      return;
    }

    if (controller.signal.aborted) return;

    if (probeResult.warnings?.length) {
      setVideoUploadState(prev => ({ ...prev, warning: probeResult.warnings[0] }));
    }

    setVideoUploadState(prev => ({
      ...prev,
      stage: "uploading",
      progress: 0,
    }));

    try {
      const sceneName = stripExtension(file.name) || "360 Video";
      const registeredData = await uploadAndRegisterVideoScene({
        tourId,
        videoFile: file,
        posterBlob: probeResult.posterBlob,
        sceneName,
        signal: controller.signal,
        onProgress: ({ percentage }) => {
          setVideoUploadState(prev => {
            const nextStage = percentage >= 100 ? "registering" : "uploading";
            return {
              ...prev,
              stage: nextStage,
              progress: percentage,
            };
          });
        },
      });

      if (!isPersistedId(registeredData?.id)) {
        throw new Error("Scene registration did not return a valid scene ID.");
      }

      let newSceneId = null;
      updateTour(prev => {
        const nextOrder = (prev.scenes?.length || 0) + 1;
        const newScene = mapSceneFromApi({
          ...registeredData,
          order: nextOrder,
          hotspots: [],
        });
        newSceneId = newScene.id;
        const scenes = [...prev.scenes, newScene];
        return {
          ...prev,
          scenes,
          startSceneId: prev.startSceneId ?? newScene.id,
        };
      });

      if (newSceneId != null) {
        viewerRef.current?.cancelReposition?.();
        viewerRef.current?.requestDefaultLook?.();
        setActiveSceneId(newSceneId);
        setTransitionKey(key => key + 1);
        setAddHotspotMode(false);
        setEditingHotspot(null);
      }

      toast.success("360° Video scene added successfully.");
    } catch (err) {
      if (err?.name === "AbortError" || controller.signal.aborted) {
        toast.info("360° video upload cancelled.");
      } else {
        const msg = getApiErrorMessage(
          err,
          "Failed to upload and register 360° video."
        );
        toast.error(msg);
      }
    } finally {
      revokeVideoProbeUrls(probeResult);
      videoAbortControllerRef.current = null;
      setUploading(false);
      setVideoUploadState({
        active: false,
        fileName: "",
        fileSize: 0,
        stage: "",
        progress: 0,
        warning: null,
      });
    }
  };

  const handleSelectScene = sceneId => {
    if (sceneId === activeSceneId) return;
    viewerRef.current?.cancelReposition?.();
    viewerRef.current?.requestDefaultLook?.();
    setActiveSceneId(sceneId);
    setTransitionKey(key => key + 1);
    setAddHotspotMode(false);
    editingHotspotOriginalRef.current = null;
    setEditingHotspot(null);
  };

  const handleHotspotSceneChange = sceneId => {
    if (sceneId === activeSceneId) return;
    viewerRef.current?.preserveLook?.();
    setActiveSceneId(sceneId);
    setTransitionKey(key => key + 1);
    setAddHotspotMode(false);
    editingHotspotOriginalRef.current = null;
    setEditingHotspot(null);
  };

  const handleRenameScene = (sceneId, name) => {
    updateTour(prev => renameScene(prev, sceneId, name));
  };

  const handleReorderScenes = useCallback(
    (fromIndex, toIndex) => {
      updateTour(prev => reorderScenes(prev, fromIndex, toIndex));
    },
    [updateTour]
  );

  const handleUpdateSceneView = (sceneId, patch) => {
    updateTour(prev => updateSceneDefaultView(prev, sceneId, patch));
  };

  const handleResetCamera = () => {
    viewerRef.current?.resetCamera();
  };

  const handleDeleteScene = sceneId => {
    updateTour(prev => {
      const nextTour = deleteScene(prev, sceneId);
      const remaining = nextTour.scenes;
      if (!remaining.length) {
        setActiveSceneId(null);
      } else if (activeSceneId === sceneId) {
        const deletedIndex = prev.scenes.findIndex(scene => scene.id === sceneId);
        const nextId =
          remaining[Math.min(deletedIndex, remaining.length - 1)]?.id ?? remaining[0].id;
        setActiveSceneId(nextId);
        setTransitionKey(key => key + 1);
      }
      return nextTour;
    });
    viewerRef.current?.cancelReposition?.();
    setAddHotspotMode(false);
    setEditingHotspot(null);
  };

  const handleAddHotspot = () => {
    if (!activeSceneId) return;
    viewerRef.current?.cancelReposition?.();
    setAddHotspotMode(true);
    editingHotspotOriginalRef.current = null;
    setEditingHotspot(null);
  };

  const handleSphereClick = ({ yaw, pitch }) => {
    if (!addHotspotMode || !activeSceneId) return;
    const draft = createHotspotDraft({ yaw, pitch });
    setEditingHotspot(draft);
    setEditingHotspotIsNew(true);
    setHotspotErrors({});
    setAddHotspotMode(false);
    updateTour(prev => upsertHotspot(prev, activeSceneId, draft));
  };

  const handleEditHotspot = useCallback(hotspot => {
    viewerRef.current?.cancelReposition?.();
    if (!hotspot) return;
    setEditingHotspot(current => {
      if (current && current.id === hotspot.id) {
        return current;
      }
      editingHotspotOriginalRef.current = { ...hotspot };
      return { ...hotspot };
    });
    setEditingHotspotIsNew(false);
    setHotspotErrors({});
    setAddHotspotMode(false);
  }, []);

  const handleHotspotDoubleClick = hotspot => {
    viewerRef.current?.cancelReposition?.();
    if (!hotspot) return;
    if (!isTextHotspot(hotspot) && hotspot.targetSceneId) {
      handleHotspotSceneChange(hotspot.targetSceneId);
    } else if (isTextHotspot(hotspot) && hotspot.webUrl?.trim()) {
      openHotspotUrl(hotspot);
    }
  };

  const handleHotspotMove = useCallback(
    (hotspot, { yaw, pitch }) => {
      if (!activeSceneId || !hotspot) return;
      if (
        Math.abs(hotspot.yaw - yaw) < 0.0001 &&
        Math.abs(hotspot.pitch - pitch) < 0.0001
      ) {
        return;
      }
      updateTour(prev => ({
        ...prev,
        scenes: prev.scenes.map(scene => {
          if (scene.id !== activeSceneId) return scene;
          return {
            ...scene,
            hotspots: (scene.hotspots ?? []).map(item =>
              item.id === hotspot.id ? { ...item, yaw, pitch } : item
            ),
          };
        }),
      }));
      setEditingHotspot(current => {
        if (current && current.id === hotspot.id) {
          return {
            ...current,
            yaw,
            pitch,
          };
        }
        return current;
      });
    },
    [activeSceneId, updateTour]
  );

  const handleHotspotChange = nextHotspot => {
    setEditingHotspot(nextHotspot);
    setHotspotErrors({});
    if (activeSceneId && nextHotspot?.id) {
      updateTour(prev => ({
        ...prev,
        scenes: prev.scenes.map(scene => {
          if (scene.id !== activeSceneId) return scene;
          const hotspots = [...(scene.hotspots ?? [])];
          const index = hotspots.findIndex(item => item.id === nextHotspot.id);
          if (index >= 0) hotspots[index] = { ...hotspots[index], ...nextHotspot };
          else hotspots.push(nextHotspot);
          return { ...scene, hotspots };
        }),
      }));
    }
  };

  const cancelHotspotEdit = () => {
    if (editingHotspotIsNew && editingHotspot) {
      revokeHotspotDraftUrls(editingHotspot);
      updateTour(prev => deleteHotspot(prev, activeSceneId, editingHotspot.id));
    } else if (editingHotspot && editingHotspotOriginalRef.current) {
      const orig = editingHotspotOriginalRef.current;
      if (orig.id === editingHotspot.id) {
        updateTour(prev => ({
          ...prev,
          scenes: prev.scenes.map(scene => {
            if (scene.id !== activeSceneId) return scene;
            return {
              ...scene,
              hotspots: (scene.hotspots ?? []).map(item =>
                item.id === orig.id ? { ...item, ...orig } : item
              ),
            };
          }),
        }));
      }
    }
    editingHotspotOriginalRef.current = null;
    setEditingHotspot(null);
    setEditingHotspotIsNew(false);
    setHotspotErrors({});
    setAddHotspotMode(false);
  };

  const handleSaveHotspot = () => {
    if (!editingHotspot || !activeSceneId) return;
    const errors = validateHotspot(editingHotspot);
    if (Object.keys(errors).length) {
      setHotspotErrors(errors);
      return;
    }
    updateTour(prev =>
      upsertHotspot(
        prev,
        activeSceneId,
        normalizeHotspotForSave(editingHotspot, activeSceneId)
      )
    );
    editingHotspotOriginalRef.current = null;
    setEditingHotspot(null);
    setEditingHotspotIsNew(false);
    setHotspotErrors({});
  };

  const handleDeleteHotspot = hotspotId => {
    if (!activeSceneId) return;
    updateTour(prev => deleteHotspot(prev, activeSceneId, hotspotId));
    if (editingHotspot?.id === hotspotId) {
      editingHotspotOriginalRef.current = null;
      setEditingHotspot(null);
      setEditingHotspotIsNew(false);
    }
  };

  const resolveTargetName = targetSceneId =>
    getTargetSceneName(tour, targetSceneId);

  const loadError = !tourId
    ? "Missing tour id. Create the tour from the 360 Tours list."
    : isTourError
    ? getApiErrorMessage(tourError, "Failed to load tour")
    : "";
  const isHydrating = Boolean(tourId) && (isTourLoading || isTourFetching) && !tourData;
  const viewerEmptyMessage = loadError
    ? loadError
    : isHydrating
    ? "Loading tour..."
    : "Select or upload a scene to preview";

  return (
    <CmsThemeProvider>
      <Head title="360 Tour Editor" />
      <div className={styles.editor}>
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <button type="button" className={styles.backBtn} onClick={handleBack}>
              ← Back
            </button>
            <input
              type="text"
              className={styles.titleInput}
              value={tour.title}
              onChange={event =>
                updateTour(prev => ({ ...prev, title: event.target.value }))
              }
              aria-label="Tour title"
            />
          </div>
          <div className={styles.headerRight}>
            {saveStatus && <span className={styles.statusText}>{saveStatus}</span>}
            <Button
              type="primary"
              onClick={handleSaveTour}
              loading={isSaving}
              disabled={!tourId || uploading || isHydrating}
            >
              Save Tour
            </Button>
          </div>
        </header>

        <div className={styles.body}>
          <aside className={styles.sidebar}>
            <ScenesSidebarSection
              scenes={tour.scenes}
              activeSceneId={activeSceneId}
              onSelectScene={handleSelectScene}
              onAddScenes={handleAddScenes}
              onAddVideoScene={handleAddVideoScene}
              onRenameScene={handleRenameScene}
              onDeleteScene={handleDeleteScene}
              onReorderScenes={handleReorderScenes}
              onUpdateSceneView={handleUpdateSceneView}
              onResetCamera={handleResetCamera}
              uploading={uploading}
            />
            <HotspotsSidebarSection
              hotspots={activeScene?.hotspots ?? []}
              addHotspotMode={addHotspotMode}
              activeSceneId={activeSceneId}
              getTargetSceneName={resolveTargetName}
              onAddHotspot={handleAddHotspot}
              onEditHotspot={handleEditHotspot}
              onDeleteHotspot={handleDeleteHotspot}
              onOpenHotspotUrl={openHotspotUrl}
            />
          </aside>

          <main className={styles.viewerArea}>
            {activeScene?.imageUrl || activeScene?.videoUrl ? (
              <PanoramaEditorViewer
                ref={viewerRef}
                scene={activeScene}
                transitionKey={transitionKey}
                editorMode={addHotspotMode}
                hotspotInteraction="edit"
                selectedHotspotId={editingHotspot?.id}
                onSceneChange={handleHotspotSceneChange}
                onSphereClick={handleSphereClick}
                onHotspotClick={handleEditHotspot}
                onHotspotDoubleClick={handleHotspotDoubleClick}
                onHotspotMove={handleHotspotMove}
                tourScenes={tour.scenes}
              />
            ) : (
              <div className={styles.emptyViewer}>{viewerEmptyMessage}</div>
            )}

            {addHotspotMode && (
              <div className={styles.placementHint}>
                Click anywhere on the panorama to place a hotspot
              </div>
            )}

            <HotspotEditorPanel
              hotspot={editingHotspot}
              scenes={tour.scenes}
              activeSceneId={activeSceneId}
              errors={hotspotErrors}
              onChange={handleHotspotChange}
              onSave={handleSaveHotspot}
              onCancel={cancelHotspotEdit}
            />
          </main>
        </div>
      </div>
      <Modal
        title="Unsaved changes"
        open={leaveModalOpen}
        onCancel={() => setLeaveModalOpen(false)}
        centered
        footer={[
          <Button key="discard" onClick={handleDiscardAndLeave} disabled={isSaving}>
            Go back
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleSaveAndLeave}
            loading={isSaving}
          >
            Save changes
          </Button>,
        ]}
      >
        <p className={styles.leaveModalText}>
          Your changes are not saved. Going back will remove the changes.
        </p>
      </Modal>

      <Modal
        title="Uploading 360° Video"
        open={videoUploadState.active}
        closable={false}
        maskClosable={false}
        centered
        footer={[
          <Button
            key="cancel"
            danger
            onClick={handleCancelVideoUpload}
          >
            Cancel Upload
          </Button>,
        ]}
      >
        <div className={styles.uploadFileName}>{videoUploadState.fileName}</div>
        <div className={styles.uploadFileSize}>
          {formatBytes(videoUploadState.fileSize)}
        </div>

        <Progress
          percent={videoUploadState.progress}
          status={
            videoUploadState.stage === "registering"
              ? "active"
              : videoUploadState.stage === "probing"
              ? "normal"
              : "active"
          }
          strokeColor={{
            "0%": "#00f0ff",
            "100%": "#c084fc",
          }}
        />

        <div className={styles.uploadStageText}>
          {videoUploadState.stage === "probing" && (
            <span>Analyzing 360° video & extracting poster frame...</span>
          )}
          {videoUploadState.stage === "uploading" && (
            <span>Uploading video to storage ({videoUploadState.progress}%)...</span>
          )}
          {videoUploadState.stage === "registering" && (
            <span>Verifying and registering 360° video scene...</span>
          )}
        </div>

        {videoUploadState.warning && (
          <div className={styles.uploadWarning}>
            <Alert
              type="warning"
              message={videoUploadState.warning}
              showIcon
            />
          </div>
        )}
      </Modal>
    </CmsThemeProvider>
  );
};

export default TourEditorPage;
